import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Email Marketing Systems Built for Retention and Revenue",
  seoDescription: "Email marketing built for founders who care about retention, lifecycle value, and measurable growth. Not broadcast campaigns.",
  canonicalUrl: "https://xperfomax.com/email-marketing",
  category: "services",
  categoryLabel: "Services / Email Marketing",
  headline: "Email Marketing Systems Built for Retention and Revenue",
  subheadline: "Email marketing built for founders who care about retention, lifecycle value, and measurable growth. Not broadcast campaigns.",
  ctaText: "Start a growth conversation",
  problemTitle: "Why Email Campaigns Fail",
  problems: [
    "Broadcast campaigns treat all subscribers the same.",
    "Automation exists but triggers on schedules, not behavior.",
    "Open rates are celebrated while revenue contribution is unknown.",
    "Retention is underfunded compared to acquisition.",
  ],
  systemTitle: "Our Email System",
  systemDescription: "Lifecycle-based segmentation, automation-first strategy, and revenue-focused messaging.",
  services: [
    { name: "Campaign Strategy", icon: "Target", components: ["Campaign planning & calendar", "Audience segmentation", "A/B testing frameworks"], outcome: "Campaigns that drive opens, clicks, and revenue." },
    { name: "Automation & Flows", icon: "Workflow", components: ["Welcome & onboarding sequences", "Abandoned cart recovery", "Post-purchase flows"], outcome: "Always-on revenue from behavior-triggered emails." },
    { name: "CRM Integration", icon: "Users", components: ["Platform configuration", "Data synchronization", "Custom field mapping"], outcome: "Unified customer data powering personalized messaging." },
    { name: "Retention & Re-engagement", icon: "RefreshCw", components: ["Win-back campaigns", "Churn prevention flows", "Loyalty program emails"], outcome: "Maximize customer lifetime value through retention." },
    { name: "Email Reporting & Optimization", icon: "BarChart3", components: ["Revenue attribution", "Performance dashboards", "Continuous optimization"], outcome: "Clear visibility into what drives revenue, not just opens." },
    { name: "Deliverability & List Health", icon: "Mail", components: ["Sender reputation management", "List hygiene & segmentation", "Inbox placement optimization"], outcome: "Your emails reach the inbox, not spam." },
  ],
  processTitle: "From audit to automation.",
  processSteps: [
    { number: "01", title: "Lifecycle Audit", description: "Analyze current email performance, identify lifecycle gaps, and map the complete customer journey." },
    { number: "02", title: "Strategy & Flow Design", description: "Define trigger-based flows, segmentation logic, and messaging strategy aligned with business outcomes." },
    { number: "03", title: "Platform & Integration", description: "Configure email infrastructure, integrate data sources, and build the technical foundation." },
    { number: "04", title: "Content & Sequence Build", description: "Create high-converting email sequences, templates, and personalization logic." },
    { number: "05", title: "Launch & Optimize", description: "Deploy flows with monitoring, A/B testing, and continuous optimization based on revenue attribution." },
  ],
  timelinePhases: [
    { number: "01", title: "Lifecycle Audit", description: "Analyze current performance and map the customer journey.", decision: "Which lifecycle stages are underperforming.", output: "Gap analysis with revenue opportunity sizing.", riskRemoved: "Blind spots in customer journey." },
    { number: "02", title: "Strategy & Flow Design", description: "Define trigger-based flows and segmentation logic.", decision: "What behaviors trigger what messages.", output: "Flow architecture with messaging framework.", riskRemoved: "Random campaigns without system logic." },
    { number: "03", title: "Platform & Integration", description: "Configure infrastructure and integrate data sources.", decision: "How data flows between systems.", output: "Connected email infrastructure with clean data.", riskRemoved: "Data silos and integration failures." },
    { number: "04", title: "Content & Sequence Build", description: "Create high-converting sequences and templates.", decision: "What messaging converts at each stage.", output: "Deployed sequences with A/B frameworks.", riskRemoved: "Generic messaging that doesn't resonate." },
    { number: "05", title: "Launch & Optimize", description: "Deploy with monitoring and continuous optimization.", decision: "What to scale, pause, or iterate.", output: "Revenue-attributed performance dashboards.", riskRemoved: "Vanity metrics masking real performance." },
  ],
  technologyCategories: [
    { title: "Email Platforms", icon: "Send", items: ["HubSpot", "Klaviyo", "Mailchimp", "ActiveCampaign"] },
    { title: "CRM", icon: "Database", items: ["Salesforce", "HubSpot CRM", "Pipedrive"] },
    { title: "Analytics", icon: "LineChart", items: ["GA4", "Looker Studio", "Platform Analytics"] },
    { title: "Testing", icon: "FileCheck", items: ["Litmus", "Email on Acid"] },
  ],
  techTitle: "Email Marketing Tools",
  techDescription: "Enterprise-grade email platforms and integrations.",
  qualification: {
    forYou: [
      "You have a customer list but aren't maximizing its value",
      "You're ready to invest in retention, not just acquisition",
      "You want email to be a revenue channel, not just communication",
      "You're willing to implement proper tracking and attribution",
    ],
    notForYou: [
      "You're looking for one-off campaign blasts",
      "You don't have an existing customer base",
      "You're not willing to segment your audience",
      "You expect overnight results from email",
    ],
  },
  faqs: [
    { question: "How long does it take to see results from email marketing?", answer: "Automation flows typically show results within 30 days of launch. Full lifecycle optimization compounds over 3-6 months as you build data and refine targeting." },
    { question: "What email platforms do you work with?", answer: "We're platform-agnostic but have deep expertise in HubSpot, Klaviyo, Mailchimp, and ActiveCampaign. We can recommend the right platform for your needs or work with your existing setup." },
    { question: "How do you measure email marketing ROI?", answer: "We focus on revenue attribution, not vanity metrics. We track revenue per email, flow revenue, and overall contribution to customer lifetime value." },
    { question: "Do you write the email copy?", answer: "Yes, we handle strategy, copywriting, and design. Our email sequences are written for conversion while maintaining your brand voice." },
  ],
  finalCtaTitle: "Turn email into a revenue channel.",
  finalCtaDescription: "Most brands underutilize email. Let's build a system that compounds retention and revenue.",
  finalCtaButtonText: "Start a growth conversation",
};

const EmailMarketing = () => {
  const { content } = usePageContent("email-marketing", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      timelinePhases={content.timelinePhases}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default EmailMarketing;
